/**
 * Result processing utility functions
 * 
 * Handles processing of matchmaking results including:
 * - Fetching and mapping provider profiles
 * - Merging profiles with match results
 * - Mapping to UI format
 * - Updating matchmaking state
 */

import { mapApiResponseToMatches } from './matchmakingHelpers';
import { preloadProviderLogos } from './image-preloading.utils';
import { mergeProviderProfiles, createSaveResultsPayload } from './search-payload.utils';
import { fetchAndMapProviderProfiles } from './search-results.utils';
import { PAGE_SIZE } from '../config/matchmaking.config';
import type { ApiResponse, ApiMatchResult } from './matchmakingHelpers';

type MatchmakingState = ReturnType<typeof import('../hooks/useMatchmakingState').useMatchmakingState>;

/**
 * Parameters for processing search results
 */
type ProcessSearchResultsParams = {
  matchmakingApiData: ApiResponse;
  matchResultsRecord: Record<string, ApiMatchResult>;
  searchId: number | null;
  advisorProfileId: string | null;
  selectedClientId: string | null;
  loadCount: number;
  isLoadMore: boolean;
  currentMatches: number;
  matchmakingState: MatchmakingState;
  saveResultsMutation: any;
  setLoadCount: (count: number) => void;
  setError: (error: string | null) => void;
  setSearchCounter: (updater: (prev: number) => number) => void;
  setIsViewingHistoryResults: (value: boolean) => void;
  setShouldFetchHistory: (value: boolean) => void;
};

/**
 * Processes and updates matchmaking state with search results
 * 
 * Handles the complete result processing flow:
 * 1. Saves results (for new searches)
 * 2. Fetches and maps provider profiles
 * 3. Merges profiles with match results
 * 4. Maps to UI format
 * 5. Updates matchmaking state
 * 
 * @param params - Result processing parameters
 * @returns Promise that resolves when processing is complete
 */
export const processSearchResults = async (params: ProcessSearchResultsParams): Promise<void> => {
  const {
    matchmakingApiData,
    matchResultsRecord,
    searchId,
    advisorProfileId,
    selectedClientId,
    loadCount,
    isLoadMore,
    currentMatches,
    matchmakingState,
    saveResultsMutation,
    setLoadCount,
    setError,
    setSearchCounter,
    setIsViewingHistoryResults,
    setShouldFetchHistory,
  } = params;

  // Save results for new searches
  if (!isLoadMore && searchId && advisorProfileId && selectedClientId) {
    try {
      const saveResultsPayload = createSaveResultsPayload(
        searchId,
        advisorProfileId,
        selectedClientId,
        matchResultsRecord,
      );
      await saveResultsMutation.mutateAsync(saveResultsPayload);
    } catch (saveResultsError) {
      // Silent fail - results still displayed even if save fails
    }
  }

  // Fetch and map provider profiles
  let providerProfilesMap: Record<string, any> = {};
  if (searchId && advisorProfileId && selectedClientId) {
    try {
      const currentLoadCount = isLoadMore ? loadCount + 1 : 1;
      setLoadCount(currentLoadCount);

      providerProfilesMap = await fetchAndMapProviderProfiles(
        searchId,
        advisorProfileId,
        selectedClientId,
        loadCount,
        isLoadMore,
      );

    } catch (backendError) {
      console.error('[MATCHMAKING DEBUG] Error fetching provider profiles:', backendError);
      if (!isLoadMore) {
        matchmakingState.setClientMatches({
          matches: [],
          append: false,
          hasMore: false,
        });
      }
      setError(
        backendError instanceof Error
          ? backendError.message
          : 'Something went wrong. Please try again.',
      );
      return;
    }
  }

  // Merge provider profiles with match results
  const mergedProviderProfiles = mergeProviderProfiles(providerProfilesMap, matchResultsRecord);

  // Map API response to match format
  const mergedData = {
    ...matchmakingApiData,
    provider_profiles: mergedProviderProfiles,
  };

  let mappedMatches = mapApiResponseToMatches(mergedData);

  mappedMatches = await preloadProviderLogos(mappedMatches);

  // Update state with results
  const totalResults = mappedMatches.length;
  const hasMoreResults = isLoadMore
    ? totalResults > currentMatches && totalResults >= PAGE_SIZE
    : totalResults >= PAGE_SIZE;


  if (isLoadMore) {
    const newMatches = mappedMatches.slice(currentMatches);
    matchmakingState.setClientMatches({
      matches: newMatches,
      append: true,
      hasMore: hasMoreResults,
    });
  } else {
    matchmakingState.setClientMatches({
      matches: mappedMatches,
      append: false,
      hasMore: hasMoreResults,
    });
    setSearchCounter((prev) => prev + 1);
    setIsViewingHistoryResults(false);
    setShouldFetchHistory(true);
  }

};

